<template>
  <div class="c-app flex-row align-items-center min-vh-100 position-relative overflow-hidden cyber-theme">
    <div class="tech-background">
      <div class="grid-background"></div>
      
      <div v-for="i in 15" :key="'particle-' + i" class="particle" :style="getParticleStyle(i)"></div>

      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>

      <div class="tech-icon-float tech-icon-1">
        <CIcon name="cil-settings" size="custom" class="tech-svg" />
      </div>

      <div class="tech-icon-float tech-icon-2">
        <CIcon name="cil-bolt" size="custom" class="tech-svg" />
      </div>

      <div class="holo-line holo-line-1"></div>
      <div class="holo-line holo-line-2"></div>
    </div>

    <CContainer>
      <CRow class="justify-content-center">
        <CCol md="8" lg="6" xl="5">

          <transition name="zoom-fade" appear>
            <CCard class="glass-card" @mouseenter.native="isHovered = true" @mouseleave.native="isHovered = false">
              
              <div class="corner-accent corner-tl"></div>
              <div class="corner-accent corner-tr"></div>
              <div class="corner-accent corner-bl"></div>
              <div class="corner-accent corner-br"></div>

              <CCardBody class="p-5 text-center position-relative content-layer mobile-padding-adjust">
                
                <div class="mb-5 logo-section mobile-margin-adjust">
                  <div class="logo-container mb-4">
                    <div class="rotating-rings">
                      <div class="ring ring-1"></div>
                      <div class="ring ring-2"></div>
                    </div>

                    <div class="logo-glow"></div>

                    <div class="logo-wrapper">
                      <img src="@/assets/logo.svg" height="110" class="mfu-logo" alt="MFU Logo" />
                    </div>
                  </div>

                  <div class="text-wrapper">
                    
                    <h1 class="brand-title brand-title-2">{{ $t('login.title') }}</h1>
                    <h2 class="brand-subtitle">{{ $t('login.subtitle') }}</h2>
                  </div>
                </div>

                <div class="auth-section">
                  <button class="btn-google-cyber mb-3" @click="triggerGoogleLogin" type="button">
                    <div class="btn-shine"></div>
                    
                    <div class="btn-energy"></div>

                    <div class="icon-wrapper">
                      <span class="google-g">G</span>
                    </div>
                    <span class="btn-text">{{ $t('login.signIn') }}</span>
                  </button>

                  <TestLoginPanel v-if="testLoginEnabled" />

                  <p class="warning-text mb-4">
                    <CIcon name="cil-warning" class="warning-icon"/>
                    {{ $t('login.mfuHint') }}
                  </p>

                  <button class="btn-manual-ghost" @click="onAuthenGoogle">
                    <CIcon name="cil-book" size="sm" class="mr-2" /> {{ $t('login.userManual') }}
                  </button>
                </div>

                <div class="footer-info mt-5">
                  <div class="divider-tech"></div>
                  <small class="copyright">© 2026 Copyright by Mae Fah Luang University. All rights reserved.</small>
                </div>

              </CCardBody>
            </CCard>
          </transition>

        </CCol>
      </CRow>
    </CContainer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Swal from 'sweetalert2';
import TestLoginPanel from '@/components/testing/TestLoginPanel';

export default {
  name: 'SignIn',
  components: { TestLoginPanel },
  data: function () {
    return {
      authClient: null,
      isHovered: false,
      testLoginEnabled: process.env.NODE_ENV === 'development',
    }
  },
  mounted() {
    this.checkAndInitGoogleAuth();
  },
  methods: {
    checkAndInitGoogleAuth() {
      if (typeof window.google !== 'undefined') {
        this.initGoogleAuth();
      } else {
        const interval = setInterval(() => {
          if (typeof window.google !== 'undefined') {
            clearInterval(interval);
            this.initGoogleAuth();
          }
        }, 300);
      }
    },
    initGoogleAuth() {
      if (typeof window.google !== 'undefined') {
        this.authClient = window.google.accounts.oauth2.initCodeClient({
          client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
          scope: 'openid profile email',
          ux_mode: 'popup',
          callback: (response) => {
            if (response.code) {
              this.sendCodeToBackend(response.code);
            }
          },
        });
      }
    },
    triggerGoogleLogin() {
      if (!this.authClient && typeof window.google !== 'undefined') {
        this.initGoogleAuth();
      }

      if (this.authClient) {
        this.authClient.requestCode();
      } else {
        Swal.fire({
          icon: 'warning',
          title: this.$t('login.swalGoogleLoadingTitle'),
          text: this.$t('login.swalGoogleLoadingText'),
          confirmButtonText: this.$t('login.ok')
        });
      }
    },
    async sendCodeToBackend(code) {
      try {
        Swal.fire({
          title: this.$t('login.verifyingTitle'),
          text: this.$t('login.verifyingText'),
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        
        await this.$store.dispatch("auth/signIn", { code: code });
        
        Swal.fire({
          icon: 'success',
          title: this.$t('login.successTitle'),
          showConfirmButton: false,
          timer: 1500
        });
      } catch (err) {
        console.error("Backend error:", err);
        const errorMsg = err.response && err.response.data && err.response.data.message 
                          ? err.response.data.message 
                          : this.$t('login.failGeneric');
        Swal.fire({
          icon: 'error',
          title: this.$t('login.errorTitle'),
          text: errorMsg,
          confirmButtonText: this.$t('login.ok')
        });
      }
    },
    getParticleStyle(index) {
      const size = Math.random() * 6 + 2;
      const colors = ['#2ba9a5', '#d4af37', '#3dd5d0'];
      return {
        width: size + 'px',
        height: size + 'px',
        background: colors[index % 3],
        left: Math.random() * 100 + '%',
        top: Math.random() * 100 + '%',
        animationDuration: (Math.random() * 10 + 15) + 's',
        animationDelay: (Math.random() * 5) + 's'
      };
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;600;800&display=swap');

/* --- BASE THEME --- */
.cyber-theme {
  font-family: 'Sarabun', sans-serif;
  background: radial-gradient(circle at top right, #1a4f4d 0%, #0c2b2a 100%);
  min-height: 100vh;
}


/* --- TECH BACKGROUND --- */
.tech-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
  /* ไม่ให้ชั้นพื้นหลังดักคลิก — ลูกที่มี pointer-events: auto ยังรับคลิกได้ถ้าไม่บังคับ */
  pointer-events: none;
}

.tech-background * {
  pointer-events: none;
}

/* --- ANIMATED GRID BACKGROUND --- */
.grid-background {
  position: absolute;
  width: 200%;
  height: 200%;
  background-image: 
    linear-gradient(rgba(43, 169, 165, 0.15) 1px, transparent 1px),
    linear-gradient(90deg, rgba(43, 169, 165, 0.15) 1px, transparent 1px);
  background-size: 50px 50px;
  transform: perspective(500px) rotateX(60deg) translateZ(-100px);
  animation: gridMove 20s linear infinite;
}

@keyframes gridMove {
  0% { transform: perspective(500px) rotateX(60deg) translateZ(-100px) translateY(0); }
  100% { transform: perspective(500px) rotateX(60deg) translateZ(-100px) translateY(50px); }
}

/* --- FLOATING PARTICLES --- */
.particle {
  position: absolute;
  border-radius: 50%;
  opacity: 0.3;
  animation: float ease-in-out infinite;
  pointer-events: none;
}

@keyframes float {
  0%, 100% { transform: translateY(0) translateX(0); }
  50% { transform: translateY(-30px) translateX(20px); }
}

/* --- GRADIENT ORBS --- */
.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: #14b8a6;
  top: -100px;
  left: -100px;
  opacity: 0.2;
  animation: pulse 8s ease-in-out infinite;
}

.orb-2 {
  width: 320px;
  height: 320px;
  background: #fbbf24;
  bottom: -50px;
  right: -50px;
  opacity: 0.15;
  animation: floatOrb 12s ease-in-out infinite alternate;
}

@keyframes floatOrb {
  0% { transform: translate(0, 0); }
  100% { transform: translate(-40px, -40px); }
}

/* --- TECH ICONS --- */
.tech-icon-float {
  position: absolute;
  opacity: 0.05;
  pointer-events: none;
}

.tech-icon-1 {
  top: 15%;
  right: 15%;
  animation: spin-slow 20s linear infinite;
}

.tech-icon-2 {
  bottom: 15%;
  left: 15%;
  animation: floatRotate 15s ease-in-out infinite;
}

.tech-svg {
  width: 160px !important;
  height: 160px !important;
  color: #2ba9a5;
}

@keyframes spin-slow {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes floatRotate {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-30px) rotate(15deg); }
}

/* --- HOLOGRAPHIC LINES --- */
.holo-line {
  position: absolute;
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(43, 169, 165, 0.4), transparent);
  opacity: 0.3;
  animation: pulse 2s ease-in-out infinite;
  pointer-events: none;
}

.holo-line-1 {
  top: 33.33%;
}

.holo-line-2 {
  top: 66.66%;
  animation-delay: 1s;
}

/* --- GLASS CARD --- */
.glass-card {
  position: relative;
  background: rgba(255, 255, 255, 0.08) !important;
  backdrop-filter: blur(30px) !important;
  -webkit-backdrop-filter: blur(30px) !important;
  border: 1px solid rgba(255, 255, 255, 0.18) !important;
  border-radius: 28px !important;
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.5),
    inset 0 0 80px rgba(255, 255, 255, 0.02) !important;
  overflow: visible !important;
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  z-index: 10;
}

.glass-card:hover {
  transform: translateY(-8px) scale(1.01);
  box-shadow: 
    0 30px 80px rgba(43, 169, 165, 0.25),
    inset 0 0 100px rgba(255, 255, 255, 0.04) !important;
  border-color: rgba(43, 169, 165, 0.3) !important;
}

.content-layer {
  position: relative;
  z-index: 20;
}

/* --- CORNER ACCENTS --- */
.corner-accent {
  position: absolute;
  width: 80px;
  height: 80px;
  opacity: 0.4;
  pointer-events: none;
  z-index: 5;
}

.corner-tl {
  top: -1px;
  left: -1px;
  border-left: 2px solid #2ba9a5;
  border-top: 2px solid #2ba9a5;
  border-top-left-radius: 28px;
}

.corner-tr {
  top: -1px;
  right: -1px;
  border-right: 2px solid #d4af37;
  border-top: 2px solid #d4af37;
  border-top-right-radius: 28px;
}

.corner-bl {
  bottom: -1px;
  left: -1px;
  border-left: 2px solid #d4af37;
  border-bottom: 2px solid #d4af37;
  border-bottom-left-radius: 28px;
}

.corner-br {
  bottom: -1px;
  right: -1px;
  border-right: 2px solid #2ba9a5;
  border-bottom: 2px solid #2ba9a5;
  border-bottom-right-radius: 28px;
}

/* --- LOGO SECTION --- */
.logo-container {
  position: relative;
  display: inline-block;
  margin-bottom: 2rem;
}

.rotating-rings {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 150px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ring {
  position: absolute;
  border-radius: 50%;
}

.ring-1 {
  width: 130px;
  height: 130px;
  border: 2px solid rgba(43, 169, 165, 0.3);
  animation: spin-slow 12s linear infinite;
}

.ring-2 {
  width: 150px;
  height: 150px;
  border: 2px solid rgba(212, 175, 55, 0.2);
  animation: spin-reverse 15s linear infinite;
}

@keyframes spin-reverse {
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
}

.logo-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120px;
  height: 120px;
  background: radial-gradient(circle, rgba(61, 213, 208, 0.4) 0%, transparent 70%);
  border-radius: 50%;
  animation: pulse-glow 3s infinite;
}

@keyframes pulse-glow {
  0% { transform: translate(-50%, -50%) scale(1); opacity: 0.4; }
  50% { transform: translate(-50%, -50%) scale(1.15); opacity: 0.6; }
  100% { transform: translate(-50%, -50%) scale(1); opacity: 0.4; }
}

.logo-wrapper {
  position: relative;
  width: 110px;
  height: 110px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mfu-logo {
  position: relative;
  z-index: 2;
  filter: drop-shadow(0 10px 20px rgba(20, 86, 84, 0.5));
  transition: transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.logo-wrapper:hover .mfu-logo {
  transform: scale(1.1);
}

/* --- BRAND TITLES --- */
.text-wrapper {
  position: relative;
}

.brand-title {
  font-weight: 800;
  font-size: 2rem;
  letter-spacing: -0.5px;
  text-transform: uppercase;
  margin-bottom: 0.3rem;
  background-size: 200% auto;
  line-height: 1.2;
}

.brand-title-1 {
  background: linear-gradient(90deg, #2ba9a5, #3dd5d0, #2ba9a5);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: shimmer 3s ease-in-out infinite;
}

.brand-title-2 {
  background: linear-gradient(90deg, #2ba9a5, #14b8a6, #2ba9a5);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: shimmer 3s ease-in-out infinite;
  animation-delay: 0.5s;
}

@keyframes shimmer {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.brand-subtitle {
  color: #cbd5e1;
  font-weight: 600;
  font-size: 1.1rem;
  letter-spacing: 2px;
  margin-top: 0.5rem;
}

/* --- GOOGLE BUTTON --- */
.btn-google-cyber {
  width: 100%;
  height: 64px;
  background: linear-gradient(135deg, #145654 0%, #1e7e7a 50%, #2ba9a5 100%);
  border: none;
  border-radius: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0 12px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 12px 35px rgba(20, 86, 84, 0.4);
}

.btn-google-cyber:hover {
  transform: scale(1.03);
  box-shadow: 0 18px 45px rgba(20, 86, 84, 0.6);
}

.btn-google-cyber:active {
  transform: scale(0.98);
}

.btn-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 1s;
}

.btn-google-cyber:hover .btn-shine {
  left: 100%;
}

.btn-energy {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #3dd5d0;
  opacity: 0;
  transition: opacity 0.3s;
}

.btn-google-cyber:hover .btn-energy {
  opacity: 0.15;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.1; }
  50% { opacity: 0.2; }
}

.icon-wrapper {
  width: 48px;
  height: 48px;
  background: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  z-index: 2;
  transition: transform 0.3s;
}

.btn-google-cyber:hover .icon-wrapper {
  transform: rotate(8deg);
}

.google-g {
  font-family: 'Arial', sans-serif;
  font-weight: 900;
  font-size: 26px;
  background: linear-gradient(45deg, #4285f4, #34a853, #fbbc05, #ea4335);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.btn-text {
  flex: 1;
  text-align: center;
  color: white;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  z-index: 2;
  text-shadow: 0 2px 8px rgba(0,0,0,0.2);
  padding-right: 48px;
  white-space: nowrap; 
}

/* --- WARNING TEXT --- */
.warning-text {
  color: #f87171;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.warning-icon {
  color: #f87171 !important;
  animation: pulse-warning 2s infinite;
}

@keyframes pulse-warning {
  0%, 100% { opacity: 0.8; }
  50% { opacity: 1; }
}

.warning-text strong {
  color: #fca5a5;
}

/* --- MANUAL BUTTON --- */
.btn-manual-ghost {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 28px;
  border: 2px solid rgba(148, 163, 184, 0.3);
  border-radius: 50px;
  background: transparent;
  color: #94a3b8;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  backdrop-filter: blur(8px);
}

.btn-manual-ghost:hover {
  border-color: #2ba9a5;
  color: #2ba9a5;
  background: rgba(43, 169, 165, 0.08);
  transform: translateY(-2px);
}

.btn-manual-ghost svg {
  transition: transform 0.3s;
}

.btn-manual-ghost:hover svg {
  transform: rotate(12deg);
}

/* --- FOOTER --- */
.divider-tech {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.3), transparent);
  width: 70%;
  margin: 0 auto 1rem auto;
}

.copyright {
  color: #64748b;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  display: block;
  line-height: 1.4;
}

/* --- TRANSITIONS --- */
.zoom-fade-enter-active {
  transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.zoom-fade-enter {
  opacity: 0;
  transform: scale(0.9) translateY(40px);
}

.zoom-fade-enter-to {
  opacity: 1;
  transform: scale(1) translateY(0);
}

/* --- MOBILE RESPONSIVENESS (ปรับปรุงใหม่) --- */
@media (max-width: 576px) {
  /* ซ่อน Orbs เพื่อความสะอาดตา */
  .orb {
    display: none; 
  }

  /* 🔥 1. เพิ่ม Margin ให้การ์ด เพื่อให้มีพื้นที่ว่างซ้ายขวา ไม่อึดอัด */
  .glass-card {
    margin-left: 24px !important;  /* เว้นระยะซ้าย */
    margin-right: 24px !important; /* เว้นระยะขวา */
  }

  /* ปรับ Padding ด้านในให้น้อยลง เพื่อชดเชยกับ Margin ที่เพิ่มขึ้น */
  .mobile-padding-adjust {
    padding: 2rem 1.5rem !important; 
  }

  /* ลด Margin ของส่วน Logo */
  .mobile-margin-adjust {
    margin-bottom: 2rem !important; 
  }

  /* ย่อขนาด Logo */
  .mfu-logo {
    height: 80px; 
  }
  
  .logo-wrapper {
    width: 80px;
    height: 80px;
  }

  /* ย่อขนาดวงแหวนและแสง */
  .logo-glow {
    width: 90px;
    height: 90px;
  }
  .ring-1 {
    width: 100px;
    height: 100px;
  }
  .ring-2 {
    width: 120px;
    height: 120px;
  }

  /* ลดขนาด Font หัวข้อ */
  .brand-title {
    font-size: 1.5rem; 
  }
  .brand-subtitle {
    font-size: 0.9rem;
  }

  /* ปรับปุ่ม Google ให้เล็กลงและตัวหนังสือบรรทัดเดียว */
  .btn-google-cyber {
    height: 50px; 
    padding: 0 8px; 
  }

  .icon-wrapper {
    width: 36px;
    height: 36px;
  }

  .google-g {
    font-size: 20px;
  }

  .btn-text {
    font-size: 0.95rem; 
    padding-right: 36px; 
    white-space: nowrap; /* บังคับให้อยู่บรรทัดเดียว */
  }

  /* ปรับปุ่มคู่มือ */
  .btn-manual-ghost {
    padding: 8px 20px;
    font-size: 0.9rem;
  }
}
</style>
